class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.cart-upsell__list');
    this.errorEl = this.querySelector('.cart-upsell__error');
    this.limit = parseInt(this.dataset.limit, 10) || 3;
    this.inCart = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
    this.list.addEventListener('click', this.onClick.bind(this));
    this.load();
  }

  fromProductsJson(p, root) {
    const prices = p.variants.map((v) => parseFloat(v.price));
    return {
      id: p.id,
      title: p.title,
      url: `${root}products/${p.handle}`,
      image: p.images[0] && p.images[0].src,
      price: Math.round(Math.min(...prices) * 100),
      variants: p.variants,
      available: p.variants.some((v) => v.available),
    };
  }

  async load() {
    const root = this.dataset.rootUrl || '/';
    let picks = [];
    try {
      // One group per cart item (newest first): its collection handles, smallest/most specific first.
      const groups = (this.dataset.collections || '')
        .split('|')
        .map((g) => g.split(',').filter(Boolean));
      const cache = {};
      const getCollection = (handle) =>
        (cache[handle] ||= fetch(`${root}collections/${handle}/products.json?limit=60`)
          .then((r) => (r.ok ? r.json() : { products: [] }))
          .then((d) => d.products)
          .catch(() => []));

      const buckets = [];
      for (const handles of groups) {
        // Most specific collection first, then broader ones to fill the remaining slots.
        const bucket = [];
        for (const handle of handles) {
          (await getCollection(handle)).forEach((p) => {
            if (!this.inCart.has(String(p.id)) && p.variants.some((v) => v.available) && !bucket.some((b) => b.id === p.id)) {
              bucket.push(p);
            }
          });
          if (bucket.length >= this.limit) break;
        }
        if (bucket.length) buckets.push(bucket);
      }
      // Interleave buckets so each cart item's collection is represented.
      const seen = new Set();
      for (let i = 0; picks.length < this.limit && buckets.some((b) => i < b.length); i++) {
        for (const b of buckets) {
          const p = b[i];
          if (p && !seen.has(p.id) && picks.length < this.limit) {
            seen.add(p.id);
            picks.push(this.fromProductsJson(p, root));
          }
        }
      }
      if (!picks.length && this.dataset.productId) {
        const res = await fetch(`${root}recommendations/products.json?product_id=${this.dataset.productId}&limit=${this.limit}&intent=related`);
        if (res.ok) {
          const { products } = await res.json();
          picks = products
            .filter((p) => p.available && !this.inCart.has(String(p.id)))
            .map((p) => ({ id: p.id, title: p.title, url: p.url, image: p.featured_image, price: p.price, variants: p.variants, available: true }));
        }
      }
    } catch (e) {
      console.error('cart-upsell:', e);
    }
    if (!picks.length) {
      this.classList.add('cart-upsell--empty');
      return;
    }
    this.list.innerHTML = picks.map((p) => this.card(p)).join('');
  }

  esc(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : str;
    return d.innerHTML.replace(/"/g, '&quot;');
  }

  money(cents) {
    const fmt = window.Shopify && Shopify.formatMoney;
    if (fmt && window.theme && theme.moneyFormat) return fmt(cents, theme.moneyFormat);
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: cur,
    }).format(cents / 100);
  }

  card(p) {
    const variants = p.variants.filter((v) => v.available !== false);
    const single = variants.length === 1;
    const img = p.image
      ? `<img src="${this.esc(p.image)}${p.image.includes('?') ? '&' : '?'}width=400" alt="${this.esc(p.title)}" width="200" height="200" loading="lazy">`
      : '';
    const action = single
      ? `<button type="button" class="cart-upsell__add button button--secondary" data-variant-id="${variants[0].id}">${this.dataset.addLabel}</button>`
      : `<a href="${this.esc(p.url)}" class="cart-upsell__add button button--secondary">${this.dataset.optionsLabel}</a>`;
    return `<li class="cart-upsell__item">
      <a href="${this.esc(p.url)}" class="cart-upsell__image">${img}</a>
      <div class="cart-upsell__info">
        <a href="${this.esc(p.url)}" class="cart-upsell__title">${this.esc(p.title)}</a>
        <span class="cart-upsell__price">${this.money(p.price)}</span>
      </div>
      ${action}
    </li>`;
  }

  async onClick(event) {
    const btn = event.target.closest('button[data-variant-id]');
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('loading');
    this.errorEl.hidden = true;
    try {
      const res = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(btn.dataset.variantId), quantity: 1 }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add item');
      const state = { id: data.items?.[0]?.id, sections: data.sections };
      document.querySelector('cart-drawer').renderContents(state);
    } catch (e) {
      this.errorEl.textContent = e.message;
      this.errorEl.hidden = false;
      btn.disabled = false;
      btn.classList.remove('loading');
    }
  }
}

customElements.define('cart-upsell', CartUpsell);
